import i18next from "i18next";
import getPercentileColor from "../../util/getPercentileColor";
import SRS from "../../../../services/StudentRecordService";
import { getCurrentScreeningWindowForSchoolYear } from "../../../../services/screeningWindowService";
import getPercentileRank from "../../services/parent/getPercentileRank";

/**
 * Generates subscores using reading rope report as a data source.
 * @param {string} studentId
 * @param {string} locale
 */
export async function generateSubscores(studentId, locale = "EN_US", useARM, isEarlyReader, assessments = [], benchmark, customBenchmarks, isBenchmarkOnly) {
  const useLatestARM = false; // always false is intended for this use case
  const { readingRopeReport } = await SRS.getReadingRopeData({ studentId, locale, useARM, useLatestARM });
  return generateSubscoresFromReadingRope(readingRopeReport, locale, isEarlyReader, assessments, benchmark, customBenchmarks, isBenchmarkOnly);
}

export function generateSubscoresFromReadingRope(readingRope, locale, isEarlyReader, assessments = [], benchmark, customBenchmarks, isBenchmarkOnly) {
  if (!readingRope) return null;
  if (!readingRope.threads) return null;
  if (!readingRope.threads.length) return null;

  const domains = [].concat(...readingRope.threads.map(({ domains }) => domains || []));
  const scoreDate = assessments[0]?.createdAt || null;
  const assessmentScores = {
    decoding: assessments[0]?.scores?.decodeScore || null,
    phonologicalAwareness: assessments[0]?.scores?.phonAwareScore || null,
    highFrequencyWords: assessments[0]?.scores?.esriScore || null,
    vocabulary: assessments[0]?.scores?.vocabSize || null,
  };

  return {
    decoding: transformDomainToSubScore(domains.find(d => d.label === 'Decoding'), locale, isEarlyReader, assessmentScores.decoding, benchmark, customBenchmarks, scoreDate, isBenchmarkOnly),
    phonologicalAwareness: transformDomainToSubScore(domains.find(d => d.label === 'Phonological Awareness'), locale, isEarlyReader, assessmentScores.phonologicalAwareness, benchmark, customBenchmarks, scoreDate, isBenchmarkOnly),
    highFrequencyWords: transformDomainToSubScore(domains.find(d => d.label === 'Sight Recognition'), locale, isEarlyReader, assessmentScores.highFrequencyWords, benchmark, customBenchmarks, scoreDate, isBenchmarkOnly),
    backgroundKnowledge: transformDomainToSubScore(domains.find(d => d.label === 'Background Knowledge'), locale, isEarlyReader, undefined, benchmark, customBenchmarks, scoreDate, isBenchmarkOnly),
    structuresAndReasoning: transformDomainToSubScore(domains.find(d => d.label === 'Structure & Reasoning'), locale, isEarlyReader, undefined, benchmark, customBenchmarks, scoreDate, isBenchmarkOnly),
    vocabulary: transformDomainToSubScore(domains.find(d => d.label === 'Vocabulary'), locale, isEarlyReader, assessmentScores.vocabulary, benchmark, customBenchmarks, scoreDate, isBenchmarkOnly),
  };
}

function transformDomainToSubScore(domain, locale, isEarlyReader, score, benchmark, customBenchmarks, scoreDate, isBenchmarkOnly) {
  if (!domain) return null;

  const {mastery} = domain;
  const t = i18next.getFixedT(null, 'teacher_parent_report');
  let clientCalculatedPR = null;
  let pacing = mastery.pacing;

  const DOMAIN_MAPPING = {
    'Decoding': 'decodeScore',
    'Phonological Awareness': 'phonAwareScore',
    'Sight Recognition': 'esriScore',
    'Vocabulary': 'vocabSize',
  };

  if (!isEarlyReader && DOMAIN_MAPPING[domain.label] && isBenchmarkOnly) {
    if (score !== undefined) {
      const percentileRank = getPercentileRank(score, {value: DOMAIN_MAPPING[domain.label]}, benchmark, customBenchmarks, locale, scoreDate);
      clientCalculatedPR = percentileRank.percentile;
      pacing = percentileRank.pacing;
    } else {
      clientCalculatedPR = null;
    }
  }

  return {
    percentile: clientCalculatedPR !== null ? clientCalculatedPR : mastery.value,
    color: getPercentileColor(pacing),
    statusText: t(pacing),
    description: t(`domain_descriptions.${domain.label}`),
    label: domain.label,
    pacing: pacing,
  };
}